import { gql } from 'graphql-request';

/**
 * The grid-backed species queries for the new portfolio page set, served by Go
 * over the global H3 grid rather than the Neo4j projection.
 *
 * Deliberately absent, and not oversights:
 *  - predictionCreatedAt / predictionLastUpdated — the grid has no per-species
 *    timestamps, so there is no "Added" column and no New/Updated badges.
 *  - lastPredictionRun — replaced by `snapshot`, the provenance of this answer
 *    rather than a pipeline run timestamp.
 *
 * The Red List set is CR/EN/VU/NT and non-invasive — the same set the projection
 * uses, so the figures are comparable side by side. The threatened subset
 * (CR/EN/VU) is reported separately: NT is on the Red List but is not threatened.
 */

export type NextSpeciesIucnCount = {
    category: string;
    count: number;
};

export type NextPortfolioSiteRef = {
    siteId: string;
    siteName: string;
};

export type NextSpeciesSummary = {
    snapshot: string;
    /** redListSpeciesCount + invasiveSpeciesCount, as the current page defines it. */
    totalSpeciesCount: number;
    siteCount: number;
    redListSpeciesCount: number;
    redListSiteCount: number;
    /** The threatened subset of redListSpeciesCount: CR + EN + VU. */
    threatenedSpeciesCount: number;
    threatenedSiteCount: number;
    invasiveSpeciesCount: number;
    invasiveSiteCount: number;
    iucn: NextSpeciesIucnCount[];
    sitesWithoutGeometry: NextPortfolioSiteRef[];
};

export type NextSpeciesListItem = {
    scientificName: string;
    commonName: string | null;
    iucnRedListCategory: string;
    confidence: string;
    /** Sites predicting the species at all — presence, not list membership. */
    siteCount: number;
    /** First appeared in the served snapshot — new until the next monthly roll (DD-6012). */
    isNew: boolean;
    /**
     * Sites where the species is invasive / counts on the Red List (DD-5796).
     * Null when the server predates the figures: unknown, never zero.
     */
    invasiveSiteCount: number | null;
    redListSiteCount: number | null;
};

export type NextSpeciesListResponse = {
    snapshot: string;
    species: NextSpeciesListItem[];
    pagination: {
        page: number;
        pageSize: number;
        total: number;
    };
};

export const nextSpeciesSummaryQuery = gql`
    query getPortfolioSpeciesSummary($customerId: String!, $filters: [CustomFilterOptionInput]) {
        portfolio {
            getPortfolioSpeciesSummary(customerId: $customerId, filters: $filters) {
                snapshot
                totalSpeciesCount
                siteCount
                redListSpeciesCount
                redListSiteCount
                threatenedSpeciesCount
                threatenedSiteCount
                invasiveSpeciesCount
                invasiveSiteCount
                iucn {
                    category
                    count
                }
                sitesWithoutGeometry {
                    siteId
                    siteName
                }
            }
        }
    }
`;

const speciesListFields = `
    snapshot
    species {
        scientificName
        commonName
        iucnRedListCategory
        confidence
        siteCount
        isNew
        invasiveSiteCount
        redListSiteCount
    }
    pagination {
        page
        pageSize
        total
    }
`;

export const nextRedListSpeciesQuery = gql`
    query getPortfolioRedListSpecies(
        $customerId: String!
        $filters: [CustomFilterOptionInput]
        $pagination: PaginationInput!
        $sortBy: SortByInput
        $searchText: String
    ) {
        portfolio {
            getPortfolioRedListSpecies(
                customerId: $customerId
                filters: $filters
                pagination: $pagination
                sortBy: $sortBy
                searchText: $searchText
            ) {
                ${speciesListFields}
            }
        }
    }
`;

export const nextInvasiveSpeciesQuery = gql`
    query getPortfolioInvasiveSpecies(
        $customerId: String!
        $filters: [CustomFilterOptionInput]
        $pagination: PaginationInput!
        $sortBy: SortByInput
        $searchText: String
    ) {
        portfolio {
            getPortfolioInvasiveSpecies(
                customerId: $customerId
                filters: $filters
                pagination: $pagination
                sortBy: $sortBy
                searchText: $searchText
            ) {
                ${speciesListFields}
            }
        }
    }
`;

export type NextSpeciesSummaryQueryResult = {
    portfolio: {
        getPortfolioSpeciesSummary: NextSpeciesSummary;
    };
};

export type NextRedListSpeciesQueryResult = {
    portfolio: {
        getPortfolioRedListSpecies: NextSpeciesListResponse;
    };
};

export type NextInvasiveSpeciesQueryResult = {
    portfolio: {
        getPortfolioInvasiveSpecies: NextSpeciesListResponse;
    };
};

// The grid-backed species detail behind the species info modal: which of the
// filtered sites predict one species, and how. Detection is composed server-side
// from Neo4j, so this one response carries both halves.
export const nextSpeciesDetailQuery = gql`
    query getPortfolioSpeciesDetail($customerId: String!, $scientificName: String!, $filters: [CustomFilterOptionInput]) {
        portfolio {
            getPortfolioSpeciesDetail(customerId: $customerId, scientificName: $scientificName, filters: $filters) {
                snapshot
                scientificName
                sites {
                    siteId
                    siteName
                    country
                    confidence
                    invasive
                    hasEDNAData
                    hasUploadedData
                    foundInEDNA
                    foundInUploadedData
                }
            }
        }
    }
`;

export type NextSpeciesDetailSite = {
    siteId: string;
    siteName: string;
    country: string;
    confidence: string;
    invasive: boolean;
    hasEDNAData: boolean;
    hasUploadedData: boolean;
    foundInEDNA: boolean;
    foundInUploadedData: boolean;
};

export type NextSpeciesDetail = {
    snapshot: string;
    scientificName: string;
    sites: NextSpeciesDetailSite[];
};

export type NextSpeciesDetailQueryResult = {
    portfolio: {
        getPortfolioSpeciesDetail: NextSpeciesDetail;
    };
};
